import {jwtDecode} from 'jwt-decode'
import {useMemo} from 'react'
import {AuthService} from '@/services/authService'

interface UserPermissions {
  isAdmin: boolean
  isProgramOwner: (programOwners?: string[]) => boolean
  userEmail?: string
  hasRole: (role: string) => boolean
  userInfo: {
    roles: string[]
    email?: string
  } | null
}

/**
 * Hook to check user permissions from the JWT token
 * Uses the backend token that was exchanged during login
 * @returns Object with permission check functions
 */
export const useUserPermissions = (): UserPermissions => {
  const userInfo = useMemo(() => {
    try {
      // Get the backend token (same as Angular's getToken() method)
      // This is the token that contains user roles, not the Okta token
      const accessToken = AuthService.getToken()
      if (!accessToken) return null

      // Decode the backend JWT token (same structure as Angular expects)
      const decodedToken = jwtDecode<{
        // Angular expects this structure based on IUser interface
        sub: string
        details: {
          email?: string
          FirstName?: string
          LastName?: string
          GPID?: string
          uid?: string
          AuthType?: string
        }
        roles: string[]
        exp: number
        [key: string]: any
      }>(accessToken)

      // Extract roles from the backend token (same as Angular's getUser().roles)
      const roles = decodedToken.roles || []

      // Extract email from details (same as Angular's getUser().details.email)
      const email = decodedToken.details?.email?.toLowerCase() || undefined

      return {
        roles,
        email,
      }
    } catch (error) {
      console.error('Error getting user permissions:', error)
      return null
    }
  }, []) // No dependencies needed since we're not doing token exchange here

  // Check if user has admin role (ROLE_ADMIN) - same logic as Angular
  const isAdmin = useMemo(() => {
    if (!userInfo?.roles) return false
    return userInfo.roles.includes('ROLE_ADMIN')
  }, [userInfo])

  // Check if user is a program owner
  const isProgramOwner = (programOwners?: string[]): boolean => {
    if (!userInfo?.email || !programOwners || !Array.isArray(programOwners) || programOwners.length === 0) {
      return false
    }
    return programOwners.some(owner => owner.toLowerCase() === userInfo.email)
  }

  // Function to check if user has a specific role
  const hasRole = (role: string): boolean => {
    if (!userInfo?.roles) return false
    return userInfo.roles.includes(role)
  }

  return {
    isAdmin,
    isProgramOwner,
    userEmail: userInfo?.email,
    hasRole,
    userInfo,
  }
}
